import React, { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Box, Button, IconButton, LinearProgress, MenuItem, Paper, TextField, Tooltip, Typography } from "@mui/material";
import CenterFocusStrongIcon from "@mui/icons-material/CenterFocusStrong";
import DownloadIcon from "@mui/icons-material/Download";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { MAP_PERIODS } from "./useSpeciesMap";
import { CountButton, Swatch } from "./SitePies";

const NUMBER = new Intl.NumberFormat();

const SectionTitle = ({ children }) => (
  <Typography component="h3" variant="overline" color="text.secondary" sx={{ display: "block", lineHeight: 2, mb: 0.5 }}>
    {children}
  </Typography>
);

// Where the panel sits on the map, remembered in this browser (a per-viewer
// convenience: losing it only puts the panel back in its corner).
const HOME = { x: 10, y: 100 };
const STORED = "reports.speciesMapPanel.position";
const KEY_STEP = 20;
const readPosition = () => {
  try {
    const p = JSON.parse(window.localStorage.getItem(STORED));
    return Number.isFinite(p?.x) && Number.isFinite(p?.y) ? p : HOME;
  } catch (e) {
    return HOME;
  }
};
const savePosition = (p) => {
  try {
    window.localStorage.setItem(STORED, JSON.stringify(p));
  } catch (e) {
    // storage off: the panel just starts in its corner next time
  }
};

/**
 * The panel's place on the map, dragged by its title bar or moved with the
 * arrow keys on its handle, and kept inside the map.
 */
const useMovable = (ref) => {
  const [position, setPosition] = useState(readPosition);
  const drag = useRef(null);

  const clamp = useCallback(
    (p) => {
      const el = ref.current;
      const map = el?.offsetParent;
      if (!el || !map) return p;
      // keep the title bar on the map, whatever the panel's height
      const maxX = Math.max(0, map.clientWidth - el.offsetWidth);
      const maxY = Math.max(0, map.clientHeight - 48);
      return { x: Math.min(Math.max(0, p.x), maxX), y: Math.min(Math.max(0, p.y), maxY) };
    },
    [ref]
  );

  // a smaller window, or a panel that grew, can push it off the map
  useEffect(() => {
    const map = ref.current?.offsetParent;
    if (!map || typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(() => setPosition((p) => clamp(p)));
    observer.observe(map);
    return () => observer.disconnect();
  }, [ref, clamp]);

  const moveTo = (p) => {
    const next = clamp(p);
    setPosition(next);
    return next;
  };

  const handlers = {
    onPointerDown: (e) => {
      // the title bar's own buttons stay buttons
      if (e.button !== 0 || e.target.closest("button:not([data-drag-handle])")) return;
      drag.current = { x: e.clientX - position.x, y: e.clientY - position.y, at: position };
      e.currentTarget.setPointerCapture?.(e.pointerId);
      e.preventDefault();
    },
    onPointerMove: (e) => {
      if (drag.current) drag.current.at = moveTo({ x: e.clientX - drag.current.x, y: e.clientY - drag.current.y });
    },
    onPointerUp: (e) => {
      if (!drag.current) return;
      const { at } = drag.current;
      drag.current = null;
      e.currentTarget.releasePointerCapture?.(e.pointerId);
      savePosition(at);
    },
  };

  const onHandleKey = (e) => {
    const step = { ArrowLeft: [-KEY_STEP, 0], ArrowRight: [KEY_STEP, 0], ArrowUp: [0, -KEY_STEP], ArrowDown: [0, KEY_STEP] }[e.key];
    if (!step) return;
    e.preventDefault();
    savePosition(moveTo({ x: position.x + step[0], y: position.y + step[1] }));
  };

  return { position, handlers, onHandleKey };
};

/**
 * The species map's card on the main map: the period it covers, the species
 * legend with counts, the specimens it cannot place, and its downloads. It
 * folds down to its title to leave the map clear, and moves out of the way
 * when dragged by its title bar.
 */
export const SpeciesMapPanel = ({
  period,
  onPeriod,
  loading,
  error,
  legend,
  unplaced,
  hasPies,
  onOpen,
  onFit,
  onDownloadImage,
  onDownloadData,
  panelRef,
}) => {
  const [open, setOpen] = useState(true);
  const ready = !loading && !error;
  const { position, handlers, onHandleKey } = useMovable(panelRef);

  return (
    <Paper
      ref={panelRef}
      component="aside"
      aria-label="Species map"
      elevation={3}
      sx={{
        position: "absolute",
        top: position.y,
        left: position.x,
        zIndex: 1000,
        width: 300,
        maxWidth: "calc(100% - 20px)",
        maxHeight: `calc(100% - ${position.y + 10}px)`,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        fontSize: "0.8125rem",
      }}
    >
      <Box
        {...handlers}
        sx={{ display: "flex", alignItems: "center", gap: 0.5, pl: 0.5, pr: 2, pt: 1.25, pb: open ? 0.5 : 1.25, cursor: "move", touchAction: "none", userSelect: "none" }}
      >
        <Tooltip title="Drag to move, or use the arrow keys">
          <IconButton
            size="small"
            data-drag-handle
            aria-label="Move the species map panel"
            onKeyDown={onHandleKey}
            sx={{ cursor: "move", color: "text.secondary" }}
          >
            <DragIndicatorIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Typography component="h2" sx={{ fontWeight: 500, fontSize: "0.9375rem", flex: 1 }}>
          Species map
        </Typography>
        <Tooltip title={open ? "Fold away" : "Show the legend"}>
          <IconButton size="small" aria-label={open ? "Fold the species map panel" : "Open the species map panel"} aria-expanded={open} onClick={() => setOpen(!open)}>
            {open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
      </Box>
      {loading && <LinearProgress sx={{ height: 2 }} aria-label="Loading the species map" />}
      {open && (
        <Box sx={{ overflow: "auto", px: 2, pb: 1.5, pt: 1 }}>
          <TextField
            select
            size="small"
            label="Collected"
            value={period}
            onChange={(e) => onPeriod(e.target.value)}
            fullWidth
            sx={{ mb: 1.5 }}
          >
            {MAP_PERIODS.map((p) => (
              <MenuItem key={p.key} value={p.key}>
                {p.label}
              </MenuItem>
            ))}
          </TextField>
          {error && (
            <Alert severity="error" sx={{ mb: 1.5 }}>
              {error}
            </Alert>
          )}
          {ready && !legend.length && (
            <Typography color="text.secondary" sx={{ fontSize: "inherit", mb: 1 }}>
              No specimens were collected in this period.
            </Typography>
          )}
          {legend.length > 0 && (
            <>
              <SectionTitle>Species</SectionTitle>
              <Box component="ul" sx={{ listStyle: "none", m: 0, p: 0 }}>
                {legend.map((s) => {
                  const item = (
                    <Box component="li" key={s.key} sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.375 }}>
                      <Swatch color={s.color} />
                      <Box component="span" sx={{ flex: 1, minWidth: 0 }}>
                        {s.label}
                        {s.members && (
                          <Box component="span" sx={{ color: "text.secondary" }}>
                            {" "}
                            ({s.members.length})
                          </Box>
                        )}
                      </Box>
                      <Box component="span" sx={{ color: "text.secondary", fontVariantNumeric: "tabular-nums" }}>
                        {NUMBER.format(s.total)}
                      </Box>
                    </Box>
                  );
                  return s.members ? (
                    <Tooltip key={s.key} title={s.members.join(", ")} placement="right" describeChild>
                      {item}
                    </Tooltip>
                  ) : (
                    item
                  );
                })}
              </Box>
              <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 1 }}>
                A pie's area follows its site's total count. Select a pie for its species.
              </Typography>
            </>
          )}
          {unplaced.length > 0 && (
            <Box sx={{ mt: 2 }} data-testid="not-on-map">
              <SectionTitle>Not on the map</SectionTitle>
              <Box component="ul" sx={{ listStyle: "none", m: 0, p: 0 }}>
                {unplaced.map((u) => (
                  <Box component="li" key={`${u.reason}:${u.key}`} sx={{ display: "flex", alignItems: "baseline", gap: 1, py: 0.5 }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      {u.name}
                      {u.reason !== u.name && (
                        <Typography variant="caption" color="text.secondary" component="span" sx={{ display: "block" }}>
                          {u.reason}
                        </Typography>
                      )}
                    </Box>
                    <CountButton count={u.total} label={u.name} onClick={() => onOpen({ row: u.row })} />
                  </Box>
                ))}
              </Box>
            </Box>
          )}
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 2 }}>
            <Button size="small" variant="outlined" startIcon={<CenterFocusStrongIcon />} disabled={!hasPies} onClick={onFit}>
              Show all sites
            </Button>
            <Button size="small" variant="outlined" startIcon={<DownloadIcon />} disabled={!ready || !hasPies} onClick={onDownloadImage}>
              Image
            </Button>
            <Button size="small" variant="outlined" startIcon={<DownloadIcon />} disabled={!ready || !legend.length} onClick={onDownloadData}>
              Data
            </Button>
          </Box>
        </Box>
      )}
    </Paper>
  );
};
